import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { useLocale } from "@/context/LocaleContext";
import { AccountMenu } from "@/components/AccountMenu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trophy, Users, Plus, Shield, RefreshCw } from "lucide-react";
import { PROJECTS_LIST_PATH } from "@/lib/routes";
import { CURRENCY_CODES_ORDERED, currencySelectLabel, formatCurrencyAmount } from "@/lib/currencies";
import { formatApiErrorMessage } from "@/lib/apiErrorMessage";

/** Superuser live/odds-rad: undvik att Mongo-ordning alltid sätter första projektet vid reload. */
const ADMIN_LIVE_ODDS_PROJECT_ID_KEY = "betting_admin_live_odds_project_id";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { t, lang } = useLocale();
  const [projects, setProjects] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openJoin, setOpenJoin] = useState(false);
  const [name, setName] = useState("");
  const [stake, setStake] = useState(0);
  const [currencyCode, setCurrencyCode] = useState("SEK");
  const [code, setCode] = useState("");
  const [tournaments, setTournaments] = useState([]);
  const [tournamentKey, setTournamentKey] = useState("WC2026");
  const [adminOverview, setAdminOverview] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [quotaBusy, setQuotaBusy] = useState(false);
  const [liveOddsProjectId, setLiveOddsProjectId] = useState("");
  const [oddsSyncing, setOddsSyncing] = useState(false);
  const nav = useNavigate();

  const fetchAdminOverview = useCallback(async () => {
    if (!user?.is_superuser) {
      setAdminOverview(null);
      setAdminLoading(false);
      return;
    }
    setAdminLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/overview`);
      setAdminOverview(data);
    } catch (e) {
      toast.error(t("admin.loadErr"));
      setAdminOverview(null);
    } finally {
      setAdminLoading(false);
    }
  }, [user?.is_superuser, t]);

  const loadDashboard = useCallback(async () => {
    const [projRes, tourRes] = await Promise.all([
      axios.get(`${API}/projects`),
      axios.get(`${API}/tournaments`),
    ]);
    setProjects(projRes.data);
    setTournaments(Array.isArray(tourRes.data) ? tourRes.data : []);
    if (user?.is_superuser) {
      await fetchAdminOverview();
    } else {
      setAdminOverview(null);
      setAdminLoading(false);
    }
  }, [user?.is_superuser, fetchAdminOverview]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const fetchQuota = async () => {
    setQuotaBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/the-odds-quota`);
      if (!data.ok) {
        toast.error(t("admin.quotaErr", { status: data.http_status }));
        return;
      }
      const qh = data.quota_headers || {};
      const remaining = qh["x-requests-remaining"] ?? qh["x-requests-last"];
      const used = qh["x-requests-used"];
      toast.message(t("admin.quotaOkTitle"), {
        description: [remaining != null && `remaining: ${remaining}`, used != null && `used: ${used}`].filter(Boolean).join(" · ") || JSON.stringify(qh),
      });
    } catch (e) {
      toast.error(t("admin.quotaErrGeneric"));
    } finally {
      setQuotaBusy(false);
    }
  };

  const adminProjects = useMemo(() => adminOverview?.projects || [], [adminOverview?.projects]);

  useEffect(() => {
    if (!adminProjects.length) return;
    setLiveOddsProjectId((prev) => {
      if (prev && adminProjects.some((p) => p.id === prev)) return prev;
      try {
        const saved = sessionStorage.getItem(ADMIN_LIVE_ODDS_PROJECT_ID_KEY);
        if (saved && adminProjects.some((p) => p.id === saved)) return saved;
      } catch {
        /* ignore */
      }
      return adminProjects[0].id;
    });
  }, [adminProjects]);

  const onLiveOddsProjectChange = useCallback((projectId) => {
    setLiveOddsProjectId(projectId);
    try {
      sessionStorage.setItem(ADMIN_LIVE_ODDS_PROJECT_ID_KEY, projectId);
    } catch {
      /* ignore */
    }
  }, []);

  const liveOddsRow = useMemo(
    () => adminProjects.find((p) => p.id === liveOddsProjectId),
    [adminProjects, liveOddsProjectId],
  );

  const create = async () => {
    if (!name.trim()) return toast.error(t("dashboard.nameRequired"));
    const { data } = await axios.post(`${API}/projects`, {
      name,
      tournament_key: tournamentKey || "WC2026",
      stake: Number(stake) || 0,
      currency_code: currencyCode || "SEK",
    });
    setOpenCreate(false); setName(""); setStake(0); setCurrencyCode("SEK"); setTournamentKey("WC2026");
    toast.success(t("dashboard.createOk"));
    nav(`/project/${data.id}`);
  };
  const join = async () => {
    try {
      const { data } = await axios.post(`${API}/projects/join`, { invite_code: code.trim() });
      setOpenJoin(false); setCode("");
      toast.success(t("dashboard.joinOk"));
      nav(`/project/${data.id}`);
    } catch (e) { toast.error(t("dashboard.joinInvalid")); }
  };

  return (
    <div className="min-h-screen grain overflow-x-hidden">
      <header className="border-b border-[var(--line)] px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 min-w-0">
        <h1 className="font-display text-2xl sm:text-3xl tracking-wider shrink-0 min-w-0 truncate">{t("dashboard.brand")}</h1>
        <AccountMenu
          user={user}
          helpReturnTo={PROJECTS_LIST_PATH}
          onLogout={logout}
          testId="dashboard-account-menu"
        />
      </header>
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-8 sm:py-12 min-w-0 overflow-x-hidden">
        <div className="flex flex-col gap-6 mb-8 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight break-words">
              {t("dashboard.myTournaments")}
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:shrink-0">
            <Dialog open={openJoin} onOpenChange={setOpenJoin}>
              <DialogTrigger asChild>
                <button data-testid="btn-open-join" className="btn-ghost flex items-center justify-center gap-2 w-full sm:w-auto min-h-11">
                  <Users size={16} />
                  {t("dashboard.join")}
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--panel)] border-[var(--line-2)]">
                <DialogHeader><DialogTitle>{t("dashboard.joinTitle")}</DialogTitle></DialogHeader>
                <Input data-testid="input-invite-code" value={code} onChange={e=>setCode(e.target.value)} placeholder="kod..." className="bg-[var(--bg-2)] border-[var(--line-2)]"/>
                <button data-testid="btn-confirm-join" onClick={join} className="btn-primary">{t("dashboard.joinBtn")}</button>
              </DialogContent>
            </Dialog>
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
              <DialogTrigger asChild>
                <button data-testid="btn-open-create" className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto min-h-11">
                  <Plus size={18} />
                  {t("dashboard.newProject")}
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--panel)] border-[var(--line-2)]">
                <DialogHeader><DialogTitle>{t("dashboard.createTitle")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t("dashboard.createName")}</Label><Input data-testid="input-project-name" value={name} onChange={e=>setName(e.target.value)} className="bg-[var(--bg-2)] border-[var(--line-2)]"/></div>
                  <div>
                    <Label>{t("dashboard.tournamentLabel")}</Label>
                    <Select value={tournamentKey} onValueChange={setTournamentKey}>
                      <SelectTrigger data-testid="select-tournament" className="bg-[var(--bg-2)] border-[var(--line-2)] w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--panel)] border-[var(--line-2)]">
                        {(tournaments.length ? tournaments : [{ key: "WC2026", name: t("dashboard.tournamentWC") }]).map((x) => (
                          <SelectItem key={x.key} value={x.key}>{x.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("dashboard.createStake")}</Label><Input data-testid="input-stake" type="number" value={stake} onChange={e=>setStake(e.target.value)} className="bg-[var(--bg-2)] border-[var(--line-2)]"/></div>
                  <div>
                    <Label>{t("dashboard.currencyLabel")}</Label>
                    <Select value={currencyCode} onValueChange={setCurrencyCode}>
                      <SelectTrigger data-testid="select-currency" className="bg-[var(--bg-2)] border-[var(--line-2)] w-full mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--panel)] border-[var(--line-2)] max-h-72">
                        {CURRENCY_CODES_ORDERED.map((code) => (
                          <SelectItem key={code} value={code}>{currencySelectLabel(code, lang)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-[var(--text-dim)]">{t("dashboard.createHint")}</p>
                </div>
                <button data-testid="btn-confirm-create" onClick={create} className="btn-primary">{t("dashboard.createBtn")}</button>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {projects.length === 0 ? (
          <div
            className="rounded-2xl border border-[var(--line)] bg-[var(--panel)] px-6 py-10 sm:p-12 text-center"
            data-testid="empty-state"
          >
            <Trophy className="mx-auto text-[var(--accent)]" size={52} strokeWidth={1.5} />
            <p className="mt-4 text-sm sm:text-base text-[var(--text-dim)] max-w-md mx-auto leading-relaxed">
              {t("dashboard.noProjects")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {projects.map(p => (
              <Link
                key={p.id}
                to={`/project/${p.id}`}
                data-testid={`project-card-${p.id}`}
                className="rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-5 sm:p-6 hover:border-[var(--accent)] transition-colors group block"
              >
                <div className="flex items-start justify-between gap-3">
                  <Trophy className="text-[var(--accent)] shrink-0" size={28} />
                  <span className="text-xs px-2 py-1 rounded-full border border-[var(--line-2)] shrink-0">
                    {p.tournament_key === "WC2026" ? t("dashboard.badgeWC") : p.tournament_key}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl mt-4 group-hover:text-[var(--accent)] transition-colors break-words">
                  {p.name}
                </h3>
                <div className="mt-3 flex items-center justify-between gap-3 text-sm text-[var(--text-dim)]">
                  <span>{p.members?.length || 1} {t("dashboard.participants")}</span>
                  <span className="font-mono tabular-nums shrink-0">
                    {p.stake
                      ? formatCurrencyAmount(p.stake, p.currency_code || "SEK", lang)
                      : t("dashboard.stakeNone")}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {user?.is_superuser && (
          <section
            id="admin-section"
            className="mt-12 sm:mt-16 px-3 sm:px-0 pt-10 sm:pt-12 border-t border-[var(--line)] space-y-6"
            data-testid="dashboard-admin-section"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Shield className="shrink-0 text-[var(--accent)]" size={28} aria-hidden />
              <h2 className="font-display text-xl sm:text-2xl tracking-wider">{t("admin.pageTitle")}</h2>
            </div>
            <p className="text-sm text-[var(--text-dim)] leading-relaxed max-w-2xl">{t("admin.intro")}</p>

            <div className="flex flex-wrap gap-3 items-center">
              <button
                type="button"
                data-testid="admin-quota-btn"
                disabled={quotaBusy}
                onClick={fetchQuota}
                className="btn-ghost text-sm min-h-11"
              >
                {quotaBusy ? "…" : t("admin.quotaBtn")}
              </button>
              <span className="text-xs text-[var(--text-dim)]">{t("admin.quotaHint")}</span>
            </div>

            {adminOverview?.totals && (
              <div className="space-y-1 text-sm font-mono tabular-nums text-[var(--accent-2)]">
                <p>
                  {t("admin.totalCreditsLogged")}: {adminOverview.totals.the_odds_credits_logged_sum ?? 0}
                </p>
                <p>
                  {t("admin.apiSportsCallsToday")}: {adminOverview.totals.today_calls ?? 0}
                  {" · "}
                  {t("admin.apiSportsCallsTotal")}: {adminOverview.totals.total_calls ?? 0}
                  {adminOverview.totals.requests_remaining != null && (
                    <>
                      {" · "}
                      {t("admin.apiSportsRemaining")}: {adminOverview.totals.requests_remaining}
                    </>
                  )}
                </p>
              </div>
            )}

            {adminLoading ? (
              <p className="text-[var(--text-dim)]">{t("common.loading")}</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[var(--line-2)] bg-[var(--panel)]/60">
                <table className="w-full text-sm text-left min-w-[640px]">
                  <thead>
                    <tr className="border-b border-[var(--line)] text-[var(--text-dim)] uppercase tracking-wider text-xs">
                      <th className="p-3 font-medium">{t("admin.colProject")}</th>
                      <th className="p-3 font-medium">{t("admin.colTournament")}</th>
                      <th className="p-3 font-medium">{t("admin.colMembers")}</th>
                      <th className="p-3 font-medium">{t("admin.colScheduler")}</th>
                      <th className="p-3 font-medium">{t("admin.colSyncs")}</th>
                      <th className="p-3 font-medium">{t("admin.colCreditsLog")}</th>
                      <th className="p-3 font-medium">{t("admin.colLastFetch")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminProjects.map((p) => {
                      const st = p.odds_sync_stats || {};
                      const last = p.odds_last_fetched_at
                        ? new Date(p.odds_last_fetched_at).toLocaleString(lang === "en" ? "en-GB" : "sv-SE", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : "—";
                      return (
                        <tr key={p.id} className="border-b border-[var(--line)]/80 hover:bg-[var(--bg-2)]/30">
                          <td className="p-3">
                            <Link to={`/project/${p.id}`} className="text-[var(--accent)] hover:underline font-medium">
                              {p.name || p.id}
                            </Link>
                          </td>
                          <td className="p-3 font-mono text-xs">{p.tournament_key || "—"}</td>
                          <td className="p-3 tabular-nums">{p.member_count ?? 0}</td>
                          <td className="p-3">{p.odds_scheduler_enabled ? t("admin.yes") : t("admin.no")}</td>
                          <td className="p-3 tabular-nums">{st.sync_count ?? 0}</td>
                          <td className="p-3 tabular-nums font-mono text-[var(--accent-2)]">{st.the_odds_credits_total ?? "—"}</td>
                          <td className="p-3 text-xs text-[var(--text-dim)] whitespace-nowrap">{last}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {adminProjects.length === 0 && (
                  <p className="p-6 text-[var(--text-dim)] text-center">{t("admin.noProjects")}</p>
                )}
              </div>
            )}

            {!adminLoading && adminProjects.length > 0 && (
              <div className="space-y-10 pt-10 mt-10 border-t border-[var(--line)]">
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold text-[var(--accent)] uppercase tracking-wider">{t("settings.sectionLive")}</h3>
                  <div>
                    <Label className="text-xs">{t("admin.pickProjectForLiveOdds")}</Label>
                    <Select value={liveOddsProjectId} onValueChange={onLiveOddsProjectChange}>
                      <SelectTrigger
                        data-testid="superuser-live-project-select"
                        className="mt-1 bg-[var(--bg-2)] border-[var(--line-2)]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--panel)] border-[var(--line-2)]">
                        {adminProjects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name || p.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-[var(--text-dim)]">{t("settings.liveHint")}</p>
                </section>

                <section className="space-y-4">
                  <h3 className="text-sm font-semibold text-[var(--accent)] uppercase tracking-wider">{t("settings.sectionOdds")}</h3>
                  <p className="text-xs text-[var(--text-dim)]">{t("settings.oddsHint")}</p>
                  <p className="text-xs font-mono text-[var(--text-dim)]">
                    {t("settings.oddsLastLabel")}{" "}
                    {liveOddsRow?.odds_last_fetched_at
                      ? new Date(liveOddsRow.odds_last_fetched_at).toLocaleString(lang === "en" ? "en-GB" : "sv-SE", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                      : t("settings.oddsNever")}
                  </p>
                  <button
                    type="button"
                    data-testid="btn-odds-sync-super"
                    disabled={oddsSyncing || !liveOddsProjectId}
                    onClick={async () => {
                      if (!liveOddsProjectId) return;
                      setOddsSyncing(true);
                      try {
                        const { data } = await axios.post(`${API}/projects/${liveOddsProjectId}/odds/sync`);
                        const fetchedH2h = data?.fetched_h2h ?? data?.matches_with_odds ?? 0;
                        const fetchedCs = data?.fetched_cs ?? data?.matches_with_cs_top5 ?? 0;
                        if (data?.preserved || (fetchedH2h === 0 && fetchedCs === 0)) {
                          toast.warning(t("settings.oddsSyncEmpty"));
                        } else {
                          toast.success(t("settings.oddsSyncOk"));
                        }
                        await fetchAdminOverview();
                      } catch (e) {
                        toast.error(formatApiErrorMessage(e, t("settings.oddsSyncErr")) || t("settings.oddsSyncErr"));
                      } finally {
                        setOddsSyncing(false);
                      }
                    }}
                    className="btn-primary text-sm inline-flex items-center gap-2"
                  >
                    <RefreshCw size={14} className={oddsSyncing ? "animate-spin" : ""} />
                    {oddsSyncing ? t("settings.oddsSyncBusy") : t("settings.oddsSyncBtn")}
                  </button>
                  <div className="flex flex-col gap-2 pt-2 border-t border-[var(--line)]/60">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs text-[var(--text-dim)]">{t("settings.oddsScheduler")}</span>
                      <Switch
                        checked={!!liveOddsRow?.odds_scheduler_enabled}
                        disabled={!liveOddsProjectId}
                        onCheckedChange={async (on) => {
                          if (!liveOddsProjectId) return;
                          try {
                            await axios.patch(`${API}/projects/${liveOddsProjectId}/odds-settings`, {
                              odds_scheduler_enabled: on,
                            });
                            toast.success(t("common.saved"));
                            await fetchAdminOverview();
                          } catch (e) {
                            toast.error(formatApiErrorMessage(e, t("project.saveFailed")) || t("project.saveFailed"));
                          }
                        }}
                        data-testid="odds-scheduler-switch-super"
                      />
                    </div>
                    <p className="text-xs text-[var(--text-dim)]">{t("settings.oddsSchedulerHint")}</p>
                  </div>
                </section>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
